<template>
  <UsersTable
    :rows="users"
    :active="(row, _) => route?.params?.id?.toString() === row.id"
    :loading="isLoading || isDeletingUser"
    :page="currentPage"
    :searchEnabled="true"
    :searchQuery="searchQuery"
    :filters="filterCollection.filters"
    :filterConfigs="filterConfigs"
    :key="actionsCompleted"
    :has-more-items="hasNextPage"
    :removable="true"
    :editable="true"
    @click-row="redirectToUser"
    @load="fetchNextPage"
    @search="search($event)"
    @sort="setSort"
    @create="showCreateUserModal = true"
    @create-machine="showCreateMachineUserModal = true"
    @move="showMoveUserModal = true"
    @remove="
      (row: User) => {
        activeUser = row;
        showDeleteUserModal = true;
      }
    "
    @edit="
      (row: User) => {
        activeUser = row;
        showUpdateUserModal = true;
      }
    "
  />

  <!-- CREATE -->
  <MutationUserModal
    v-if="showCreateUserModal"
    @close="showCreateUserModal = false"
    @create="onCreateUser"
    :loading="isCreatingUser"
    :navigation-level="UserLevel.TENANT"
    :navigation-parent-id="model.id"
  />

  <!-- UPDATE -->
  <MutationUserModal
    v-if="showUpdateUserModal && activeUser"
    :user="activeUser"
    :loading="isUpdatingUser"
    @close="closeUpdateModal"
    @update="onUpdateUser"
    :navigation-level="UserLevel.TENANT"
    :navigation-parent-id="model.id"
  />

  <InfuseConfirmModal
    v-if="showDeleteUserModal"
    :action-title="t('iam.users.header.delete_user')"
    :action-description="t('iam.users.info.delete_user', { user: activeUser?.email || '' })"
    verification-text="delete"
    @confirm="onDeleteUser"
    @close="closeDeleteModal"
  />

  <MoveUserModal
    v-if="showMoveUserModal"
    :current-level="UserLevel.TENANT"
    :current-organisation-id="undefined"
    :current-tenant-id="model.id"
    :loading="isMovingUsers"
    @close="showMoveUserModal = false"
    @move="onMoveUsers"
  />

  <MachineUserCreateFlow
    v-if="model"
    :show="showCreateMachineUserModal"
    :create="createMachineUser"
    :parent-id="model.id"
    :navigation-level="UserLevel.TENANT"
    :loading="isCreatingMachineUser"
    @close="showCreateMachineUserModal = false"
    @created="refetch"
  />
</template>

<script setup lang="ts">
import { Tenant } from '@infuse/api/iam/tenant';
import { CreateUser, UpdateUser, User } from '@infuse/iam/user/type';
import { FilterCollection } from '@infuse/cpms/charging-station/filters';
import { useCreateTenantUser, useCreateTenantMachineUser, useDeleteTenantUser, useUpdateTenantUser } from '@infuse/iam/tenant/mutations';
import { useMoveUsers } from '@infuse/iam/user/mutations';
import { useTenantUsers } from '@infuse/iam/tenant/queries';
import { MachineUserCreateFlow, MutationUserModal, MoveUserModal, UsersTable } from '@infuse/iam/user/ui';
import { ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { InfuseConfirmModal } from '@infuse/ui/components';
import { useI18n } from 'vue-i18n';
import { UserLevel, UserMoveDestinationType } from '@infuse/iam/user/type';

const { t } = useI18n();

const props = defineProps<{
  model: Tenant;
}>();

defineEmits(['reloadModel']);

const {
  fetchParentId: fetchTenantId,
  users,
  currentPage,
  isLoading,
  hasNextPage,
  fetchNextPage,
  searchQuery,
  refetch,
  setSort
} = useTenantUsers();

const activeUser = ref<User | undefined>(undefined);

const { mutateAsync: createUser, isPending: isCreatingUser } = useCreateTenantUser();
const { mutateAsync: createMachineUser, isPending: isCreatingMachineUser } = useCreateTenantMachineUser();
const { mutateAsync: updateUser, isPending: isUpdatingUser } = useUpdateTenantUser();
const { mutateAsync: deleteUser, isPending: isDeletingUser } = useDeleteTenantUser();
const { mutateAsync: moveUsers, isPending: isMovingUsers } = useMoveUsers();

const filterCollection = new FilterCollection();
const filterConfigs = ref();

const router = useRouter();
const route = useRoute();

const actionsCompleted = ref(0); // to force re-render of the table when an action is completed

const showCreateUserModal = ref(false);
const showCreateMachineUserModal = ref(false);
const showUpdateUserModal = ref(false);
const showDeleteUserModal = ref(false);
const showMoveUserModal = ref(false);

watch(
  route,
  () => {
    fetchTenantId.value = route.params.id as string;
  },
  { immediate: true }
);

watch(
  () => route.query.search,
  (search) => {
    searchQuery.value = search?.toString();
  },
  { immediate: true }
);

async function search(search: string): Promise<void> {
  searchQuery.value = search;
  replaceQueryParams(searchQuery.value);
}

function replaceQueryParams(searchQuery: string | undefined) {
  if (router.currentRoute.value.name !== 'user') {
    // only do this if the user is currently on this page and not some sub-route.
    return;
  }

  router.replace({
    name: 'user',
    query: {
      search: searchQuery || undefined
    }
  });
}

function redirectToUser(row: User): void {
  let routeName = 'user.details';

  router.push({
    name: routeName,
    params: { id: row.id }
  });
}

async function onCreateUser(vars: { user: CreateUser }): Promise<void> {
  await createUser({ parentId: props.model.id, user: vars.user });
  showCreateUserModal.value = false;
  refetch();
}

async function onDeleteUser(): Promise<void> {
  if (!activeUser.value) {
    return;
  }

  await deleteUser({ parentId: props.model.id, id: activeUser.value.id });
  closeDeleteModal();
  refetch();
}

function closeDeleteModal() {
  showDeleteUserModal.value = false;
  activeUser.value = undefined;
}

async function onUpdateUser(vars: { id: string | undefined; user: CreateUser | UpdateUser }): Promise<void> {
  if (!vars.id || !('version' in vars.user)) {
    return;
  }
  await updateUser({ parentId: props.model.id, id: vars.id, user: vars.user });
  closeUpdateModal();
  refetch();
}

function closeUpdateModal() {
  showUpdateUserModal.value = false;
  activeUser.value = undefined;
}

async function onMoveUsers(data: {
  users: User[];
  destinationType: UserMoveDestinationType;
  destinationId?: string;
  targetTenantId?: string;
}) {
  try {
    await moveUsers({
      users: data.users,
      destinationType: data.destinationType,
      destinationId: data.destinationId,
      targetTenantId: data.targetTenantId
    });

    showMoveUserModal.value = false;
  } catch (error) {
    console.error('Error moving users:', error);
  }
}
</script>
